// Shared shapes/traversal for the forecast group hierarchy (Major Heading ->
// optional Sub Heading -> Sub Group/GL code), used by the Group Definition
// admin page as well as the Forecast and Forecast vs Actual pages.

export type GLCode = { id: number; name: string; code: string };

export type SubGroup = { id: number; categoryId: number; orderNumber: number; subHeadingId: number | null };
export type SubHeading = { id: number; name: string; orderNumber: number; color: string | null; subGroups: SubGroup[] };
export type MajorHeading = { id: number; name: string; orderNumber: number; subGroups: SubGroup[]; subHeadings: SubHeading[] };

export const flattenCategories = (cats: any[]): GLCode[] => {
    const result: GLCode[] = [];
    const walk = (list: any[]) => {
        (list || []).forEach((c) => {
            result.push({ id: c.id, name: c.name || c.code, code: c.code });
            if (c.children?.length) walk(c.children);
        });
    };
    walk(cats);
    return result;
};

// Auto-assigned Sub Heading colors, cycled by position within their Major
// Heading. A Sub Heading's own `color` (if set by the user) always wins.
export const SUB_HEADING_PALETTE = ['var(--color-primary)', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#10B981', '#6366F1', '#EF4444'];

export const resolveSubHeadingColor = (color: string | null | undefined, indexInMajorHeading: number): string =>
    color || SUB_HEADING_PALETTE[indexInMajorHeading % SUB_HEADING_PALETTE.length];

export const hexToRgba = (hex: string, alpha: number): string => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ── Period / date-bucket logic shared by Forecast and Forecast vs Actual ──────

export type PeriodType = 'month' | 'quarter' | 'year';
// asOf is set only on a bucket whose calendar period isn't fully covered by
// the fetched range (the last bucket, when the range's end - the entity's
// projection horizon - falls short of that period's actual last day) - lets
// the column header flag "this one's partial, only up to this date" instead
// of silently showing an under-counted total as if it were a complete period.
export type Bucket = { id: string; label: string; asOf?: Date };

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Coverage window: a rolling range centered on the current period (today's
// month/quarter/year), padded a fixed number of periods either side -
// monthly pads 3 months either side of the current month, quarterly pads 2
// quarters either side of the current quarter, yearly pads 1 year either
// side of the current year. Anchored to referenceDate itself (not Jan 1 of
// its year) so it moves continuously as time passes instead of jumping back
// to "3 months before January" for most of the year. If the entity's
// projection horizon (EntitySettings.projectionHorizon, surfaced as
// status.endOfHorizon by getTransactionsByEntityId - transaction-
// controller.js) reaches further into the future than that padding would,
// extend end to meet it, so Forecast/Forecast vs Actual show every period
// provisions actually get generated for instead of stopping short at a
// fixed padding.
export const getDateRange = (period: PeriodType, referenceDate: Date, horizonDate?: Date | null): { start: Date; end: Date } => {
    const year = referenceDate.getFullYear();
    let start: Date;
    let end: Date;

    if (period === 'year') {
        start = new Date(year - 1, 0, 1);
        end = new Date(year + 1, 11, 31);
    } else if (period === 'quarter') {
        const currentQuarterStartMonth = Math.floor(referenceDate.getMonth() / 3) * 3;
        start = new Date(year, currentQuarterStartMonth - 2 * 3, 1);
        end = new Date(year, currentQuarterStartMonth + 3 + 2 * 3, 0);
    } else {
        start = new Date(year, referenceDate.getMonth() - 3, 1);
        end = new Date(year, referenceDate.getMonth() + 3 + 1, 0);
    }

    if (horizonDate && horizonDate > end) {
        end = horizonDate;
    }

    return { start, end };
};

export const getBucketId = (period: PeriodType, date: Date): string => {
    const y = date.getFullYear();
    const m = date.getMonth();
    if (period === 'month') return `${y}-${String(m + 1).padStart(2, '0')}`;
    if (period === 'quarter') return `${y}-Q${Math.floor(m / 3) + 1}`;
    return `${y}`;
};

export const getBuckets = (period: PeriodType, start: Date, end: Date): Bucket[] => {
    const buckets: Bucket[] = [];
    if (period === 'month') {
        const cur = new Date(start.getFullYear(), start.getMonth(), 1);
        const last = new Date(end.getFullYear(), end.getMonth(), 1);
        while (cur <= last) {
            const bucket: Bucket = { id: getBucketId('month', cur), label: `${MONTH_ABBR[cur.getMonth()]} '${String(cur.getFullYear()).slice(2)}` };
            if (cur.getTime() === last.getTime()) {
                const periodEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
                if (end < periodEnd) bucket.asOf = end;
            }
            buckets.push(bucket);
            cur.setMonth(cur.getMonth() + 1);
        }
    } else if (period === 'quarter') {
        const cur = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
        const last = new Date(end.getFullYear(), Math.floor(end.getMonth() / 3) * 3, 1);
        while (cur <= last) {
            const bucket: Bucket = { id: getBucketId('quarter', cur), label: `Q${Math.floor(cur.getMonth() / 3) + 1} '${String(cur.getFullYear()).slice(2)}` };
            if (cur.getTime() === last.getTime()) {
                const periodEnd = new Date(cur.getFullYear(), cur.getMonth() + 3, 0);
                if (end < periodEnd) bucket.asOf = end;
            }
            buckets.push(bucket);
            cur.setMonth(cur.getMonth() + 3);
        }
    } else {
        for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
            const bucket: Bucket = { id: `${y}`, label: `${y}` };
            if (y === end.getFullYear()) {
                const periodEnd = new Date(y, 11, 31);
                if (end < periodEnd) bucket.asOf = end;
            }
            buckets.push(bucket);
        }
    }
    return buckets;
};

export const UNCATEGORIZED_KEY = 'uncategorized';

// categoryId (or UNCATEGORIZED_KEY for transactions with no category) -> bucketId -> summed amount
export const buildSumsByCategory = (transactions: any[], period: PeriodType): Record<string, Record<string, number>> => {
    const map: Record<string, Record<string, number>> = {};
    transactions.forEach(t => {
        const key = t.categoryId ? String(t.categoryId) : UNCATEGORIZED_KEY;
        const bucketId = getBucketId(period, new Date(t.date));
        if (!map[key]) map[key] = {};
        // A provision's own `amount` gets reduced (often to 0) once a real
        // transaction links/settles against it (see linkTransactions in
        // transaction-controller.js), so it stays correct for the running
        // cash-position balance elsewhere - but that means a settled
        // provision would otherwise disappear from (or undercount) the
        // Forecast total here. `originalAmount` preserves the true
        // forecasted value regardless of settlement, so provisions use that
        // instead; real transactions are never adjusted this way and keep
        // using `amount`.
        const amount = t.type === 'provision' ? (t.originalAmount ?? t.amount) : t.amount;
        map[key][bucketId] = (map[key][bucketId] || 0) + (Number(amount) || 0);
    });
    return map;
};

export type LeafCode = { categoryId: number; name: string; seed: number };
export type SubHeadingGroup = { id: number; name: string; color: string; leaves: LeafCode[] };
export type MajorHeadingGroups = { id: number; name: string; subHeadingGroups: SubHeadingGroup[]; directLeaves: LeafCode[] };

// Walks one Major Heading's Sub Headings and direct Sub Groups into a flat
// shape that's easy to render as rows. `seed` is a stable per-leaf number
// (independent of any mock/placeholder value generator) so callers can derive
// deterministic placeholder data without needing to know the hierarchy shape.
// `color` is already resolved (explicit override or palette default).
export const buildMajorHeadingGroups = (mh: MajorHeading, codeName: (categoryId: number) => string): MajorHeadingGroups => {
    const subHeadingGroups = (mh.subHeadings || []).map((sh, index) => ({
        id: sh.id,
        name: sh.name,
        color: resolveSubHeadingColor(sh.color, index),
        leaves: sh.subGroups.map(sg => ({ categoryId: sg.categoryId, name: codeName(sg.categoryId), seed: mh.id * 100000 + sh.id * 1000 + sg.id })),
    }));
    const directLeaves = (mh.subGroups || [])
        .filter(sg => !sg.subHeadingId)
        .map(sg => ({ categoryId: sg.categoryId, name: codeName(sg.categoryId), seed: mh.id * 1000 + sg.id }));
    return { id: mh.id, name: mh.name, subHeadingGroups, directLeaves };
};
